import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";

/**
 * The manual, in the app rather than beside it.
 *
 * Every figure quoted here is read back out of the running system (the payroll
 * factor, the premium multipliers, the statutory tables in force) instead of
 * being typed into prose that then drifts. A manual that states a rate the
 * software no longer uses is worse than none, because it is believed.
 */

const A = ({ children }) => (
  <span className="font-mono text-[11px] bg-secondary/60 px-1 py-0.5">{children}</span>
);

function Section({ id, n, title, lead, children }) {
  return (
    <section id={id} className="mb-10 scroll-mt-20" data-testid={`docs-${id}`}>
      <div className="flex items-baseline gap-3 mb-1">
        <span className="font-mono text-xs text-[hsl(var(--warning))] font-bold">{n}</span>
        <h2 className="font-heading font-black text-xl tracking-tight">{title}</h2>
      </div>
      {lead && <p className="text-sm text-muted-foreground mb-4 max-w-3xl">{lead}</p>}
      {children}
    </section>
  );
}

function Row({ term, children }) {
  return (
    <div className="grid md:grid-cols-[220px_1fr] gap-1 md:gap-4 py-2.5 border-b border-border last:border-0">
      <div className="font-semibold text-sm">{term}</div>
      <div className="text-sm text-muted-foreground">{children}</div>
    </div>
  );
}

function Step({ n, title, who, children }) {
  return (
    <div className="flex gap-3 py-3 border-b border-border last:border-0">
      <div className="shrink-0 w-7 h-7 border border-[hsl(var(--warning))] text-[hsl(var(--warning))] grid place-items-center font-mono text-xs font-bold">
        {n}
      </div>
      <div className="min-w-0">
        <div className="font-semibold text-sm">
          {title}
          {who && (
            <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {who}
            </span>
          )}
        </div>
        <div className="text-sm text-muted-foreground mt-0.5">{children}</div>
      </div>
    </div>
  );
}

export default function Docs() {
  const [cfg, setCfg] = useState(null);
  const [tables, setTables] = useState([]);

  useEffect(() => {
    api.get("/config").then((r) => setCfg(r.data)).catch(() => {});
    // The statutory tables are admin-editable and DATED. Read them rather than
    // printing numbers here that a later circular would silently invalidate.
    api.get("/payroll/statutory-tables").then((r) => setTables(r.data || [])).catch(() => {});
  }, []);

  const factor = cfg?.payroll_factor ?? 313;
  const unreviewed = tables.filter((t) => !t.reviewed).length;

  return (
    <div className="max-w-4xl">
      <PageHeader title="How this system works" sub="A step-by-step guide to running the agency on this system, and to the numbers underneath it." />

      <nav className="border border-border bg-card p-4 mb-8 text-sm" data-testid="docs-toc">
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-[hsl(var(--warning))] mb-2">
              Part one — doing the job
            </div>
            <ol className="space-y-1 list-none">
              {[
                ["start", "A1. Signing in, and what you land on"],
                ["ht-hr", "A2. Taking on a guard: the 201 file"],
                ["ht-armoury", "A3. Issuing and receiving a firearm"],
                ["ht-offboard", "A4. Offboarding"],
                ["ht-scheduler", "A5. Building and publishing a roster"],
                ["ht-supervisor", "A6. Running a shift"],
                ["ht-guard", "A7. The guard's phone"],
                ["ht-payroll", "A8. Closing a payroll cut-off"],
                ["ht-finance", "A9. Billing a client"],
                ["ht-admin", "A10. Users, settings and the demo switches"],
                ["ht-reserved", "A11. The six-month clock"],
                ["ht-tours", "A12. Walking the route: guard tours"],
                ["ht-benefits", "A13. 13th month, leave, the bond and a final pay"],
                ["refusals", "A14. When the system refuses"],
              ].map(([id, label]) => (
                <li key={id}>
                  <a href={`#${id}`} className="hover:text-[hsl(var(--warning))] transition-colors">{label}</a>
                </li>
              ))}
            </ol>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-2">
              Part two — how the numbers work
            </div>
            <ol className="space-y-1 list-none">
              {[
                ["one-record", "B1. One record, from hiring to offboarding"],
                ["dashboard", "B2. The dashboard, tile by tile"],
                ["chain", "B3. How a peso gets from a post to a payslip"],
                ["payroll", "B4. The payroll states"],
                ["money", "B5. How the money is actually calculated"],
                ["statutory", "B6. Government contributions"],
                ["pay-vs-bill", "B7. Pay rate vs bill rate"],
                ["wages", "B8. Wage orders and what a contract must cover"],
                ["roles", "B9. Who can do what"],
              ].map(([id, label]) => (
                <li key={id}>
                  <a href={`#${id}`} className="hover:text-[hsl(var(--warning))] transition-colors">{label}</a>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </nav>

      {/* ============================ PART ONE ============================ */}
      <div className="border-t-2 border-[hsl(var(--warning))] pt-3 mb-6">
        <div className="font-heading font-black text-2xl tracking-tight">Part one — doing the job</div>
        <p className="text-sm text-muted-foreground max-w-3xl mt-1">
          Step by step, grouped by the job you actually have. Every refusal named here is one the
          software really gives, with what to do about it.
        </p>
      </div>

      <Section
        id="start" n="A1" title="Signing in, and what you land on"
        lead="One account covers both the office console and the field app. Which one you get is decided by your role, not by the address you type."
      >
        <div className="border border-border bg-card p-4">
          <Row term="Everybody">
            Go to the site and sign in with your work email. Five wrong passwords locks the account
            for fifteen minutes — that is deliberate, and waiting is the fix.
          </Row>
          <Row term="A guard">
            Lands on the <strong>field app</strong> at <A>/m</A>: a five-tab phone screen. A guard
            cannot reach the office console at all, and does not need to.
          </Row>
          <Row term="Everyone else">
            Lands on the <strong>office console</strong>: the dashboard, with a left-hand menu
            showing only the screens your role can use. A shorter menu is not a fault — a payroll
            admin has no business on the schedule.
          </Row>
          <Row term="On a demonstration">
            The sign-in page carries a button per role. The password never leaves the server. An
            administrator can switch those buttons off from{" "}
            <Link to="/settings" className="underline">Settings</Link>, and switching them off
            makes the routes refuse as well — a hidden button that still works is not a control.
          </Row>
          <Row term="Forgot a password">
            You cannot reset your own. An administrator does it from{" "}
            <Link to="/users" className="underline">Users &amp; Roles</Link>, which also clears the
            lockout and signs the account out everywhere.
          </Row>
        </div>
      </Section>

      <Section
        id="ht-hr" n="A2" title="Taking on a guard: the 201 file"
        lead="The 201 is not a form you fill once. It is the record that decides whether this person can be rostered at all, whether payroll can remit for them, and what has to come back on their last day."
      >
        <div className="border border-border bg-card p-4">
          <Step n="1" title="Create the guard" who="hr admin">
            <Link to="/guards" className="underline">Guards</Link> → <strong>New guard</strong>.
            Name, branch, rank and daily rate. The rank is what the payslip prints to explain why
            one guard's rate differs from another's, and the daily rate is what this
            person is <em>paid</em> — not what the client is billed. See B7.
          </Step>
          <Step n="2" title="Government numbers" who="hr admin">
            Open the row and fill SSS, PhilHealth, Pag-IBIG and TIN. Each box shows the shape its
            own agency prints on the card, so an SSS number cannot quietly end up in the Pag-IBIG
            field.
            <div className="mt-1.5 text-[13px] border-l-2 border-[hsl(var(--warning))] pl-2">
              Leave one blank and the screen says so — and this guard's row in that agency's
              remittance file reads <A>** MISSING **</A> instead of an account number. Payroll
              still computes the contribution correctly; it simply cannot tell the agency whose
              account to credit, so the file cannot be filed.
            </div>
          </Step>
          <Step n="3" title="Personal particulars" who="hr admin">
            Date of birth, <strong>date hired</strong>, civil status, dependants, and a next of kin
            with a number. Date hired is separate from when the record was typed: for anybody
            migrated off a spreadsheet those are years apart, and tenure reads the real one.
            The next of kin also appears on the guard's own phone, so a responder has it.
          </Step>
          <Step n="4" title="Documents" who="hr admin">
            Security Licence, NBI and Police clearance, neuro-psychiatric and drug test, medical,
            BSGC training certificate. Give each an expiry, and tick <strong>mandatory</strong>
            for the ones that legally bar a guard from a post.
            <div className="mt-1.5 text-[13px] border-l-2 border-[hsl(var(--warning))] pl-2">
              A lapsed <em>mandatory</em> document blocks assignment outright — the scheduler
              refuses and names the document. A lapsed non-mandatory one only shows on the
              dashboard's expiry tile. Mark them accordingly: making everything mandatory empties
              your roster for a training certificate.
            </div>
          </Step>
          <Step n="5" title="Make them deployable" who="hr admin">
            Employment status <A>active</A> and the deployable switch on. Only then will the
            scheduler offer them work. Probationary, suspended and separated guards are all
            excluded, and that is the same mechanism in each case.
          </Step>
        </div>
      </Section>

      <Section
        id="ht-armoury" n="A3" title="Issuing and receiving a firearm"
        lead="Under RA 10591 the firearm sits on the AGENCY licence, not the guard's. So it is an agency asset with a custody chain — which is why it lives in the Armoury and not as a field on the guard."
      >
        <div className="border border-border bg-card p-4">
          <Step n="1" title="Put the unit on the register" who="hr admin">
            <Link to="/firearms" className="underline">Armoury</Link> → <strong>Add a firearm</strong>.
            Make, model, calibre, serial number, type, and the registration number with its expiry.
            The serial is the unit's identity: one serial, one record, and a duplicate is refused.
          </Step>
          <Step n="2" title="Issue it" who="supervisor or hr">
            <strong>Issue</strong> on the row. Pick the guard, enter the rounds going out, and the
            Duty Detail Order number it is carried under. Only active, deployable guards who are
            not already signed for something are offered — one guard, one firearm.
          </Step>
          <Step n="3" title="The guard can see it" who="guard">
            It appears on their phone under <strong>Profile → What you are signed for</strong>, with
            the serial and the round count, so they can check it against the unit in their holster
            before handing it back. It also appears on their 201 file.
          </Step>
          <Step n="4" title="Receive it back" who="supervisor">
            <strong>Receive</strong> on the row. Enter the rounds coming back and the condition.
            <div className="mt-1.5 text-[13px] border-l-2 border-[hsl(var(--alert))] pl-2">
              If fewer rounds come back than went out, the system will not let you close it without
              a written reason, and it alerts the supervisors when you do. Rounds that do not come
              back were fired, lost or taken, and those are three different conversations.
            </div>
          </Step>
          <Step n="5" title="Watch the two red banners" who="ops manager">
            The Armoury names a lapsed registration (that unit cannot be issued until it is
            renewed) and any firearm signed out to somebody who has left. The second should always
            be zero — see A4.
          </Step>
        </div>
      </Section>

      <Section
        id="ht-offboard" n="A4" title="Offboarding"
        lead="The last day is the one day the agency is most likely to lose a firearm, because the moment somebody is marked resigned nobody looks at their record again."
      >
        <div className="border border-border bg-card p-4">
          <Step n="1" title="Receive the firearm first" who="supervisor">
            If the guard is signed for one, take it back at the Armoury with its rounds counted.
            Until you do, <strong>the system will not let you separate them</strong> — it refuses
            and names the make, model, serial number and rounds outstanding, because
            &ldquo;clear your firearm&rdquo; is not something anybody can act on and
            &ldquo;return RIA-910000 and 15 rounds&rdquo; is.
          </Step>
          <Step n="2" title="Close the record" who="hr admin">
            <Link to="/guards" className="underline">Guards</Link> → set employment status to
            separated or resigned, with the date and the reason.
          </Step>
          <Step n="3" title="Nothing is deleted" who="—">
            Their shifts, attendance, incident reports, every payslip and the whole firearm custody
            chain stay on file. That is the evidence if the work, the pay or a missing round is
            ever questioned. What changes is that they leave the deployable pool, stop appearing in
            auto-fill, and the headcount on the dashboard drops.
          </Step>
          <Step n="4" title="Final pay" who="payroll admin">
            Their last cut-off runs exactly like any other — the separation does not remove them
            from a period they worked.
          </Step>
        </div>
      </Section>

      <Section
        id="ht-scheduler" n="A5" title="Building and publishing a roster"
        lead="The scheduler's job is to leave no post empty. The system's job is to refuse an assignment that would be illegal, unsafe or impossible, and to say which."
      >
        <div className="border border-border bg-card p-4">
          <Step n="1" title="Work the open shifts" who="scheduler">
            The dashboard's <strong>Open shifts (7d)</strong> tile is the to-do list: shifts with
            no guard on them. Ignored, each one becomes an unmanned post.
          </Step>
          <Step n="2" title="Assign a guard" who="scheduler">
            <Link to="/schedule" className="underline">Schedule</Link> → pick the post and the
            date, choose a guard. The system checks six things before it accepts, all listed in
            A11.
          </Step>
          <Step n="3" title="Override, if you have a reason" who="scheduler">
            A soft conflict can be overridden with a written reason, which is stored on the shift
            and on the audit log. A hard one — expired mandatory document, non-deployable, no such
            guard — is refused outright.
          </Step>
          <Step n="4" title="Publish it" who="scheduler">
            Until it is published the guard cannot see it. On publish it appears on their phone,
            and they acknowledge it.
          </Step>
          <Step n="5" title="Handle a call-off" who="scheduler">
            Call the shift off with a reason. The post reopens, coverage drops, and the record of
            who called off and why stays — which is what turns a pattern into something you can act
            on. There is no delete: removing a shift would remove the evidence that it existed.
          </Step>
        </div>
      </Section>

      <Section
        id="ht-supervisor" n="A6" title="Running a shift"
        lead="Nothing a guard does is paid or billed until a supervisor approves it. That approval is the hinge the whole money chain turns on."
      >
        <div className="border border-border bg-card p-4">
          <Step n="1" title="Watch the board" who="supervisor">
            <Link to="/attendance" className="underline">Attendance</Link> shows who has checked in
            and who has not. The dashboard's <strong>Late today</strong> tile clears itself the
            moment somebody punches.
          </Step>
          <Step n="2" title="Correct a punch" who="supervisor">
            A guard with a flat battery or no signal still worked the shift. Correct the time with
            a reason — the original stays underneath, so the correction is visible rather than a
            silent rewrite.
          </Step>
          <Step n="3" title="Approve the attendance" who="supervisor">
            This is the step that makes the hours real. Unapproved attendance is not paid to the
            guard and not billed to the client. Approval can be revoked and re-given.
          </Step>
          <Step n="4" title="Deal with an out-of-geofence punch" who="supervisor">
            A check-in outside the site's radius is still recorded — refusing it would leave the
            guard unpaid — but it is flagged, you are notified, and the distance is on the record.
          </Step>
          <Step n="5" title="Review incidents" who="supervisor">
            <Link to="/incidents" className="underline">Incidents</Link>. Reports come in from the
            field app. Amendments are versioned with a reason, so what was first said is still
            readable. High and critical reports notify you by every channel.
          </Step>
          <Step n="6" title="Answer a duress alarm" who="supervisor">
            A guard holding the SOS button on their phone raises a <strong>critical incident</strong>
            titled DURESS, with their position where the handset could give one, and notifies
            supervisors and dispatch on every channel. Treat the incident list as the record and
            the phone as the way you actually reach them.
          </Step>
        </div>
      </Section>

      <Section
        id="ht-guard" n="A7" title="The guard's phone"
        lead="Five tabs, designed to be used one-handed, in the dark, on a bad connection. This is the half of the system that most of your people will ever see."
      >
        <div className="border border-border bg-card p-4">
          <Row term="SHIFTS — check in">
            Today's assignment is outlined. <strong>Check in (GPS)</strong> records the punch with
            the distance from the post. If the phone has no fix — a basement car park, a stockroom,
            a service corridor — press <strong>QR</strong> and type the code shown on the site's
            sticker instead. That is the fallback, and it is the reason it exists.
          </Row>
          <Row term="SHIFTS — no signal at all">
            The punch is saved on the handset and a banner says how many are waiting. It syncs by
            itself when the connection comes back; a duplicate is discarded rather than paid twice.
          </Row>
          <Row term="SHIFTS — post orders and checklist">
            <strong>Post orders</strong> opens the written instructions for that site, with a
            version number and an &ldquo;I have read &amp; understood&rdquo; button.{" "}
            <strong>Checklist</strong> is the site's end-of-shift list; each tick saves as you make
            it, and an unfinished list is named before the check-out button rather than after.
          </Row>
          <Row term="SHIFTS — directions">
            Opens the post's coordinates in the phone's own maps app. For a guard sent to a site
            they have never worked, this is the difference between arriving and phoning the office.
          </Row>
          <Row term="REPORTS">
            File an incident or an occurrence report with a photograph from the camera. It reaches
            the supervisors immediately; high and critical ones page them.
          </Row>
          <Row term="ALERTS">
            Assignments, approvals, leave decisions. The badge counts what is unread.
          </Row>
          <Row term="PAY">
            Every payslip issued to this guard, with the whole arithmetic: hours by category and
            rate, gross, each of the three statutory deductions against their own membership
            number, withholding tax, anything else taken off, and the net. The PDF downloads.
          </Row>
          <Row term="PROFILE">
            What they are signed for at the armoury, who to call (duty office, supervisor, their
            own next of kin — all one tap to dial), filing leave, and their own documents with
            expiry dates.
          </Row>
          <Row term="SOS — every screen">
            Top right, on every tab. <strong>Held for two seconds</strong>, not tapped: a guard in
            trouble cannot work a confirmation dialog, and a bare button fires in a pocket. It
            sends on whatever is known and never waits on the GPS.
          </Row>
        </div>
      </Section>

      <Section
        id="ht-payroll" n="A8" title="Closing a payroll cut-off"
        lead="Three actions close a fortnight. The long way through the states is in B4; this is what you actually press."
      >
        <div className="border border-border bg-card p-4">
          <Step n="1" title="Open the period" who="payroll admin">
            <Link to="/payroll" className="underline">Payroll Prep</Link> → a name and the dates.
            A period that ends before it starts is refused, and so is one covering a fortnight
            another period already covers — two periods over one cut-off puts two different
            payslips on every guard's phone.
          </Step>
          <Step n="2" title="Compute" who="payroll admin">
            Reads every approved shift in the dates and builds one line per guard: hours by
            category, premiums, contributions, tax, net.
            <div className="mt-1.5 text-[13px] border-l-2 border-[hsl(var(--warning))] pl-2">
              Recomputing <strong>discards</strong> anything entered by hand on that period. The
              second attempt is refused, names exactly what would be lost, and only proceeds if you
              confirm.
            </div>
          </Step>
          <Step n="3" title="Adjust" who="payroll admin">
            Allowances, incentives and reimbursements; loans, cash bonds, uniform, penalties. Each
            is a named line on that guard.
            <div className="mt-1.5 text-[13px] border-l-2 border-[hsl(var(--alert))] pl-2">
              Do <strong>not</strong> add a manual deduction called SSS, PhilHealth, Pag-IBIG or
              withholding tax. The engine already computes those, and a hand-typed one deducts the
              guard twice. The run names any it finds and prints the warning on the payslip.
            </div>
          </Step>
          <Step n="4" title="Lock, then run" who="payroll admin">
            Lock freezes the figures. The run turns them into payslips and remittance files — and
            it is a snapshot: reprinting an old payslip reads what was paid, never recomputes it
            against today's rates.
          </Step>
          <Step n="5" title="Hand out and remit" who="payroll admin">
            Payslips as PDFs (or a zip of all of them), and the four contribution files — SSS,
            PhilHealth, Pag-IBIG and BIR. Each one leads with that agency's own member number. A
            guard whose number is missing shows as <A>** MISSING **</A> rather than being quietly
            dropped from the file.
          </Step>
          <Step n="6" title="Guards see their own" who="guard">
            The moment the run exists it appears on the guard's PAY tab. They do not have to ring
            the office to find out what they were paid.
          </Step>
        </div>
      </Section>

      <Section
        id="ht-finance" n="A9" title="Billing a client"
        lead="An invoice charges for approved attendance at the CONTRACT rate, plus the contract's fixed fees. What the agency pays its guards never touches it."
      >
        <div className="border border-border bg-card p-4">
          <Step n="1" title="Generate" who="finance officer">
            <Link to="/invoices" className="underline">Invoices</Link> → choose the contract and the
            period. Every completed, supervisor-approved shift for that client in those dates
            becomes a line, priced from the contract; the fixed fees are added once.
          </Step>
          <Step n="2" title="Two refusals you will meet" who="finance officer">
            A period that ends before it starts is refused — no attendance can fall inside it, so
            the invoice would go out carrying fixed fees alone. And a period another live invoice
            already covers is refused, naming that invoice, because billing the same days twice
            charges the client twice.
          </Step>
          <Step n="3" title="Top up after late approvals" who="finance officer">
            When attendance is approved after you have already billed the month, generate again
            with <strong>rebill</strong>. It charges only the shifts no live invoice covers, skips
            the fixed fees that have already gone out, and lists what it skipped on the invoice
            itself so the total does not look light for no reason.
          </Step>
          <Step n="4" title="Approve, deliver, collect" who="finance officer">
            Draft → approved → delivered, then record payments against it. Partial payments are
            normal; the dashboard's outstanding tile is total less payments received.
          </Step>
          <Step n="5" title="Withdraw one" who="finance officer">
            <strong>Void</strong> with a reason. The document stays — an invoice that vanishes from
            a client ledger is the first thing an auditor asks about — and the shifts it charged
            for become billable again. An invoice money has been received against needs a second
            confirmation, because voiding it does not unreceive the payment.
          </Step>
        </div>
      </Section>

      <Section
        id="ht-admin" n="A10" title="Users, settings and the demo switches"
        lead="Administrator-only. Two of these change what other people can see, so they are worth understanding before you touch them."
      >
        <div className="border border-border bg-card p-4">
          <Row term="Adding a person">
            <Link to="/users" className="underline">Users &amp; Roles</Link> → create the account
            with a role, and a branch scope if they should only see part of the business. A guard's
            account is linked to their guard record; that link is what makes the field app show
            them their own shifts.
          </Row>
          <Row term="Reset, edit, delete">
            A reset also clears the lockout and signs the account out everywhere. Delete is soft,
            audited and reversible, and a deleted account is refused at sign-in rather than merely
            hidden. Nobody can remove the last administrator.
          </Row>
          <Row term="Phone numbers">
            A supervisor's number on their user record is what a guard sees on the field app's
            contact card. Without it the card says &ldquo;no number on file&rdquo;.
          </Row>
          <Row term="Settings — payroll factor">
            The days-per-year used to turn a daily rate into the monthly basic the contribution
            tables are indexed on. Changing it moves every guard's bracket. It is currently{" "}
            <A>{factor}</A>.
          </Row>
          <Row term="Settings — vocabularies">
            Incident types, severities, leave types, document types, addition and deduction types.
            These fill the dropdowns; adding one here is how you avoid people typing free text into
            a field that something else routes on.
          </Row>
          <Row term="Demo data and one-click sign-in">
            Two separate switches. Hiding the demo data hides the seeded records everywhere at once
            and hides the sign-in buttons with them; it is not deletion and it is reversible. It
            refuses if a real record already points at a seeded one, and says which.
          </Row>
        </div>
      </Section>

      <Section
        id="ht-reserved" n="A11" title="The six-month clock"
        lead="A guard left off-detail for more than six continuous months is constructively dismissed under DO 150-16 — backwages plus separation pay, per guard, whether or not anybody meant it. Every fact needed to see it coming was already on file, and nothing was counting the days."
      >
        <div className="border border-border bg-card p-4">
          <Step n="1" title="Read the clock" who="ops manager or hr">
            <Link to="/reserved" className="underline">Reserved Status</Link> lists everybody
            without a detail, how many days they have been without one, and the date six months
            falls on. The count is worked out fresh every time the page loads — it is never stored,
            because a stored counter drifts the moment somebody edits a shift, and this is the one
            number that has to be right on the day a labour arbiter asks for it.
          </Step>
          <Step n="2" title="Understand where the count starts" who="everyone">
            From the guard&apos;s last real detail. If they have never had one, from their hire
            date. If they have neither, the row says <strong>unknown</strong> — which is not the
            same as fine, and means their 201 file is missing a hire date.
          </Step>
          <Step n="3" title="Repost them — the clock stops by itself" who="scheduler">
            Give the guard a shift on the <Link to="/schedule" className="underline">Schedule</Link>.
            There is no button here for it, deliberately: the roster is the record, and a note
            claiming somebody was reposted with no shift behind it looks like a defence and is not
            one.
          </Step>
          <Step n="4" title="Or offer a relocation, and record it" who="hr admin">
            <strong>Record a decision</strong> on the row. A post at comparable terms, including
            one further away, is a lawful offer; a refused offer is the agency&apos;s defence. Name
            the post you offered and what the guard said — the note is the record.
          </Step>
          <Step n="5" title="Or separate them with pay, before the six months run out" who="hr admin">
            Recorded the same way, and the money is computed in A13.
          </Step>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Warning at 120 days, critical at 150, in breach at 180. The dashboard carries the count
          so nobody has to remember to look.
        </p>
      </Section>

      <Section
        id="ht-tours" n="A12" title="Walking the route: guard tours"
        lead="Attendance says somebody was at the post. It cannot answer a client asking you to prove the basement was walked at 02:00, and a guard asleep in the shack has a perfect attendance record."
      >
        <div className="border border-border bg-card p-4">
          <Step n="1" title="Give the site a route" who="ops manager">
            <Link to="/sites" className="underline">Sites &amp; Posts</Link> → the site →
            checkpoints, and a patrol interval in hours. A site has a route when it has both. A
            static reception post genuinely has none, and leaving it without one is a real answer,
            not an omission.
          </Step>
          <Step n="2" title="The guard scans as they go" who="guard">
            Each checkpoint gets a code. The guard scans it on the phone, or taps it with GPS if
            the code is damaged; the distance from the checkpoint is recorded either way. The
            handset shows which checkpoints are still outstanding in the current round.
          </Step>
          <Step n="3" title="The round is judged at check-out" who="nobody — automatic">
            When the guard signs off, the system works out how many rounds the shift owed, how many
            were completed, and what was missed, and notifies the supervisors if anything was.
            A missed round found the same night is a supervisor sending somebody to the post; the
            same fact found at invoice time is an argument with a client.
          </Step>
          <Step n="4" title="Read the day" who="supervisor or ops">
            <Link to="/tours" className="underline">Guard Tours</Link>, by date. Only rounds whose
            window has closed are judged — a shift halfway through is not failing because its
            evening rounds have not happened yet.
          </Step>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          A round nobody started and a round abandoned halfway are different conversations, and the
          screen says which.
        </p>
      </Section>

      <Section
        id="ht-benefits" n="A13" title="13th month, leave, the bond and a final pay"
        lead="Four statutory obligations, each of which turns into a DOLE money claim when it is missed. All four are read out of the payroll runs, so they agree with the payslips the guards are holding rather than with a fresh calculation nobody has seen."
      >
        <div className="border border-border bg-card p-4">
          <Step n="1" title="13th month" who="payroll admin">
            <Link to="/benefits" className="underline">Statutory Benefits</Link> → 13th Month.
            Total <strong>basic</strong> salary earned in the year divided by twelve (PD 851),
            payable by 24 December. Basic is narrower than gross: overtime, rest-day and holiday
            premiums and night differential are all excluded, and dividing gross by twelve —
            the commonest error in a Philippine computation — overpays every guard who worked a
            night.
          </Step>
          <Step n="2" title="Service incentive leave" who="payroll admin">
            Five days a year once a guard has a year of service, and the unused days are
            <strong> commutable to cash</strong> — the half agencies forget, and the half that
            shows up in a complaint. The balance on this screen is money already owed, payable
            whether the guard takes the days or leaves without them.
          </Step>
          <Step n="3" title="The cash bond" who="payroll admin">
            A lawful deduction within limits, and refundable. The ledger is built from the
            deductions on the payslips themselves, so it cannot drift from what was actually taken.
            A guard who has left and still has a balance is named in red: that money is theirs and
            is already due.
          </Step>
          <Step n="4" title="A leaver's final pay" who="payroll admin">
            Pick the guard on the <strong>Final Pay</strong> tab. It adds the 13th month earned to
            date, the leave balance in cash and the bond to be given back, and it lists anything
            still signed out to them.
            <div className="mt-1.5 text-[13px] border-l-2 border-[hsl(var(--alert))] pl-2">
              An unreturned firearm blocks the clearance. An agency that pays the final cheque
              first has lost the only leverage it had, and the unit is on the agency&apos;s licence,
              not the guard&apos;s.
            </div>
          </Step>
          <Step n="5" title="Run a final cut-off for their last days" who="payroll admin">
            Work approved after the last cut-off is <strong>listed, not valued</strong>. A payroll
            run records the hours it computed, never which shifts it paid, so there is no honest
            way to price those days from this screen — and a confident number would short-pay
            somebody&apos;s last week while looking authoritative. Run a cut-off covering them.
          </Step>
        </div>
      </Section>

      <Section
        id="refusals" n="A14" title="When the system refuses"
        lead="Every refusal below is deliberate and names what is wrong. None of them is a fault to report — each one is the software stopping something that costs money, coverage or a licence."
      >
        <div className="border border-border bg-card p-4">
          <Row term="&ldquo;Employment status is …&rdquo;">
            Only an <A>active</A> guard can be rostered. Fix the status on the 201, or roster
            somebody else.
          </Row>
          <Row term="&ldquo;Guard is marked non-deployable&rdquo;">
            The deployable switch is off on their record. It is a deliberate hold.
          </Row>
          <Row term="&ldquo;Mandatory document expired&rdquo;">
            A document ticked mandatory has lapsed, and it is named with its expiry. Renew it on
            the 201 file.
          </Row>
          <Row term="&ldquo;Guard is on approved … leave&rdquo;">
            They have approved leave covering that date. Either they are not available, or the
            leave needs revisiting.
          </Row>
          <Row term="&ldquo;Overlaps shift …&rdquo;">
            They are already on another shift at that time; the clashing shift is named. Overridable
            with a reason.
          </Row>
          <Row term="&ldquo;Insufficient rest (&lt;8h)&rdquo;">
            Fewer than eight hours between this shift and the night shift either side of it.
            Overridable with a reason, and the reason is the point — somebody is going to be tired
            on a post.
          </Row>
          <Row term="&ldquo;A cut-off cannot end before it starts&rdquo;">
            The dates are the wrong way round. Nothing would be paid for it.
          </Row>
          <Row term="&ldquo;A payroll period already covers those dates&rdquo;">
            Compute the existing one instead. Two periods over one fortnight means two payslips.
          </Row>
          <Row term="&ldquo;A payroll run needs settled hours&rdquo;">
            Lock the period first. Computing net pay from hours somebody is still editing produces
            a payslip that disagrees with itself an hour later.
          </Row>
          <Row term="&ldquo;An invoice already covers part of this period&rdquo;">
            It names the invoice. Use <strong>rebill</strong> to charge only the work that invoice
            does not already cover, or void it first.
          </Row>
          <Row term="&ldquo;Every billable shift in this period is already invoiced&rdquo;">
            There is nothing new to charge. This is the right answer, not an error.
          </Row>
          <Row term="&ldquo;Money has been received against this invoice&rdquo;">
            Voiding it does not unreceive the payment. Decide where that money goes, then confirm.
          </Row>
          <Row term="&ldquo;That guard is already signed for a firearm&rdquo;">
            One guard, one firearm. Receive the first one back.
          </Row>
          <Row term="&ldquo;The registration on that firearm has lapsed&rdquo;">
            It cannot go out until it is renewed. The exposure is the agency's licence.
          </Row>
          <Row term="&ldquo;N round(s) are unaccounted for&rdquo;">
            Fewer rounds came back than went out. Say what happened to the difference.
          </Row>
          <Row term="&ldquo;This guard is still signed for a firearm&rdquo;">
            Offboarding is blocked. The serial and the rounds are named — recover them at the
            Armoury first.
          </Row>
          <Row term="&ldquo;Recomputing discards …&rdquo;">
            Hand-entered additions and deductions on that period would be lost. It lists them.
            Confirm only if you mean it.
          </Row>
        </div>
      </Section>

      {/* ============================ PART TWO ============================ */}
      <div className="border-t-2 border-border pt-3 mb-6 mt-12">
        <div className="font-heading font-black text-2xl tracking-tight">Part two — how the numbers work</div>
        <p className="text-sm text-muted-foreground max-w-3xl mt-1">
          Where the figures come from, and why two screens showing the same month cannot disagree.
        </p>
      </div>

      {/* ------------------------------------------------------------------ */}
      <Section
        id="one-record" n="B1" title="One record, from hiring to offboarding"
        lead="Nothing in this system is a standalone list. A guard hired today carries the same record through deployment, attendance, pay and eventually their last day — and the history stays after they leave."
      >
        <div className="border border-border bg-card p-4">
          <Row term="Hired">
            A guard is created in <Link to="/guards" className="underline">Guards</Link> with a branch,
            a rank and a daily rate. Licences and clearances go on the same record with expiry
            dates, and the dashboard starts watching those dates immediately.
          </Row>
          <Row term="Probationary → active">
            Employment status is a field on the guard. It has teeth: only an{" "}
            <A>active</A> guard can be rostered, and the scheduler refuses the assignment
            outright with the reason shown on screen.
          </Row>
          <Row term="Deployed">
            The scheduler assigns them to a post. They acknowledge on their phone, check in by GPS
            or by scanning the site's QR code, and check out at the end.
          </Row>
          <Row term="Paid">
            Their approved attendance becomes payroll lines in a cut-off period, with premiums and
            government contributions applied, and a payslip they can be handed.
          </Row>
          <Row term="Suspended">
            A disciplinary hold. Same mechanism as probation — they stop being deployable, and
            the schedule stops offering them work, without deleting anything.
          </Row>
          <Row term="Separated (offboarded)">
            Their last day. The record is <span className="text-foreground font-semibold">not deleted</span> —
            their shifts, attendance and every payslip stay on file, because that is the evidence
            you need if the work is ever questioned. What changes is that they leave the deployable
            pool, disappear from auto-fill, and the dashboard's headcount drops.
          </Row>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          The same shape applies to a client: a contract has a start and an end, and finishes either{" "}
          <A>expired</A> (it ran its term) or <A>terminated</A> (it was stopped early). Both are on
          file so you can tell the difference a year later.
        </p>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section
        id="dashboard" n="B2" title="The dashboard, tile by tile"
        lead="Every number is counted live from the records — there is no separate summary table to fall out of step. This is what each one is actually counting."
      >
        <div className="border border-border bg-card p-4">
          <Row term="Today's coverage">
            Posts filled against posts required, for today only. Required comes from each post's
            headcount at every active site; filled counts today's shifts that are not called off.
            A ratio below 100% means a post has nobody on it right now.
          </Row>
          <Row term="Open shifts (7d)">
            Shifts in the next seven days with no guard assigned. This is the scheduler's to-do
            list, and it is the number that turns into an unmanned post if it is ignored.
          </Row>
          <Row term="Late today">
            Guards whose shift has started and who have not checked in. It reads from attendance
            events, so it clears itself the moment they punch.
          </Row>
          <Row term="Open incidents">
            Incident reports not yet approved or closed. The tile also tracks how many are high or
            critical severity separately, because those are the ones a client will call about.
          </Row>
          <Row term="Deployable guards">
            Guards who are both marked deployable <em>and</em> employment status{" "}
            <A>active</A>. A suspended or separated guard is excluded here — which is exactly why
            the number drops the day someone is offboarded.
          </Row>
          <Row term="Docs expiring ≤30d">
            Licences and clearances within thirty days of expiry, and anything already expired.
            A guard with a lapsed mandatory document is blocked from assignment, so this tile is an
            early warning about coverage, not just paperwork.
          </Row>
          <Row term="Outstanding invoices">
            The unpaid balance across invoices that are approved, delivered or partially paid —
            total less payments received. Drafts are not counted; they have not been billed yet.
          </Row>
          <Row term="Latest payroll period">
            The most recent cut-off and what state it is in. See section 4.
          </Row>
        </div>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section
        id="chain" n="B3" title="How a peso gets from a post to a payslip"
        lead="This is the single most important thing to understand about the system: every figure downstream is derived, never typed twice."
      >
        <div className="border border-border bg-card p-4 font-mono text-[11px] leading-relaxed overflow-x-auto">
          <div className="whitespace-nowrap">
            client → contract → site → post → <span className="text-[hsl(var(--warning))]">shift</span> → attendance → approval
          </div>
          <div className="whitespace-nowrap pl-[13.5rem]">
            ↓
          </div>
          <div className="whitespace-nowrap pl-[10.5rem]">
            ├──→ payroll line → payslip <span className="text-muted-foreground">(what the guard is PAID)</span>
          </div>
          <div className="whitespace-nowrap pl-[10.5rem]">
            └──→ invoice line → payment <span className="text-muted-foreground">(what the client is BILLED)</span>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-3">
          One approved shift feeds both sides. Nobody re-keys hours into payroll, and nobody
          re-keys them into billing — which is why the payroll register and the invoice for the
          same month cannot disagree about how many hours were worked.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          It also means the approval step matters. Attendance that a supervisor has not approved
          is not paid and not billed.
        </p>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section
        id="payroll" n="B4" title="The payroll states"
        lead="A cut-off moves through fixed states. It can only go forwards, and once it is closed it cannot be edited."
      >
        <div className="border border-border bg-card p-4">
          <Step n="1" title="Open the period" who="payroll admin">
            Give it a name and the dates it covers — typically a fortnight. It starts{" "}
            <A>open</A>.
          </Step>
          <Step n="2" title="Compute" who="payroll admin">
            The system reads every approved shift in those dates and builds one line per guard:
            hours by category, the rate, premiums, government contributions, tax and net pay.
            <div className="mt-1.5 text-[13px] border-l-2 border-[hsl(var(--warning))] pl-2">
              Recomputing <span className="text-foreground font-semibold">discards</span> any
              additions or deductions entered by hand on that period. The system refuses the
              second time, names exactly what would be lost, and only proceeds if you confirm.
            </div>
          </Step>
          <Step n="3" title="Adjust" who="payroll admin">
            Allowances, incentives, reimbursements; loans, cash bonds, uniform, penalties. Each is
            a named line on that guard, not an unexplained figure.
          </Step>
          <Step n="4" title="Lock" who="payroll admin">
            <A>open → locked</A>. The figures stop moving. A locked period can still be sent back
            to open if something is wrong.
          </Step>
          <Step n="5" title="Approve" who="payroll admin">
            <A>locked → approved</A>. This is the sign-off.
          </Step>
          <Step n="6" title="Export" who="payroll admin">
            <A>approved → exported</A>. The register as a spreadsheet, payslips as PDFs, and the
            government contribution files for remittance.
          </Step>
          <Step n="7" title="Close" who="payroll admin">
            <A>exported → closed</A>. Finished. The period is now a permanent record.
          </Step>
        </div>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section
        id="money" n="B5" title="How the money is actually calculated"
        lead="Hours are sorted into categories, each category earns a different multiple of the hourly rate, and night hours earn a premium on top of whatever else they earned."
      >
        <div className="border border-border bg-card p-4">
          <Row term="Hourly rate">
            The guard's daily rate ÷ 8.
          </Row>
          <Row term="Regular hours">×1.00</Row>
          <Row term="Overtime">×1.25 — Labour Code Art. 87</Row>
          <Row term="Rest day / special day">×1.30 — Art. 93</Row>
          <Row term="Regular holiday">×2.00 — Art. 94</Row>
          <Row term="Paid leave">×1.00, at basic</Row>
          <Row term="Night differential">
            +10% — Art. 86, for hours between 22:00 and 06:00.{" "}
            <span className="text-foreground">This is additive.</span> A night hour on a holiday is
            a holiday hour <em>and</em> a night hour, so it earns both.
          </Row>
          <Row term="Gross">
            Everything above, plus any manual additions.
          </Row>
          <Row term="Net pay">
            Gross, less SSS, PhilHealth and Pag-IBIG employee shares, less withholding tax, less
            any manual deductions.
          </Row>
        </div>
        <div className="border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/5 p-3 mt-4">
          <div className="text-xs font-bold uppercase tracking-wider mb-1">
            Why contributions do not move when someone works overtime
          </div>
          <p className="text-sm text-muted-foreground">
            Government contributions are indexed on <span className="text-foreground">monthly basic
            pay</span>, not on what this particular fortnight happened to earn. Monthly basic is the
            daily rate × the agency's annual factor ÷ 12 — currently{" "}
            <span className="font-mono text-foreground">{factor}</span> days, set in{" "}
            <Link to="/settings" className="underline">Settings</Link>.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            If contributions were taken off gross instead, a guard would slide between SSS brackets
            from one cut-off to the next purely because of overtime, and their deductions would
            never be the same twice.
          </p>
        </div>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section
        id="statutory" n="B6" title="Government contributions"
        lead="SSS, PhilHealth, Pag-IBIG and BIR withholding are not hard-coded. They are dated tables you can read and edit, because these rates change by circular and a system that hides them is a system you cannot correct."
      >
        <div className="border border-border bg-card p-4">
          <Row term="Dated, not current">
            Each table records the date it took effect. A cut-off uses the table that was{" "}
            <span className="text-foreground">in force for that period</span> — so recomputing an
            old payroll after a rate change gives you the old, correct figures, not today's.
          </Row>
          <Row term="Reviewed vs unreviewed">
            A table ships marked <span className="text-foreground">unreviewed</span> until someone
            in your office checks it against the agency's own circular and signs it off. Payslips
            and remittance files carry that warning on their face while it is unreviewed, so an
            unchecked figure can never quietly become an official one.
          </Row>
          <Row term="Where to look">
            <Link to="/settings" className="underline">Settings → Statutory tables</Link>. Each row
            names its source document.
          </Row>
        </div>
        {tables.length > 0 && (
          <div className="mt-3 border border-border bg-card p-4" data-testid="docs-statutory-live">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-muted-foreground mb-2">
              Tables currently loaded in this system
            </div>
            <div className="space-y-1">
              {tables.map((t) => (
                <div key={t.id || `${t.kind}-${t.effective_from}`} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="font-semibold uppercase text-xs tracking-wider">{t.kind}</span>
                  <span className="font-mono text-[11px] text-muted-foreground flex-1 truncate">
                    in force from {t.effective_from}
                  </span>
                  <span className={`font-mono text-[10px] uppercase ${t.reviewed ? "text-[hsl(var(--success))]" : "text-[hsl(var(--warning))]"}`}>
                    {t.reviewed ? "reviewed" : "unreviewed"}
                  </span>
                </div>
              ))}
            </div>
            {unreviewed > 0 && (
              <div className="text-xs text-[hsl(var(--warning))] mt-2 font-semibold">
                {unreviewed} table{unreviewed > 1 ? "s have" : " has"} not been reviewed yet. Payslips will say so until they are.
              </div>
            )}
          </div>
        )}
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section
        id="pay-vs-bill" n="B7" title="Pay rate vs bill rate"
        lead="These are two different numbers and the system keeps them apart on purpose."
      >
        <div className="border border-border bg-card p-4">
          <Row term="Pay rate — the employee's">
            Set on the <Link to="/guards" className="underline">guard</Link>, by rank. A detachment
            commander and a probationary guard on the same site are paid differently, because that
            is what their ranks are worth.
          </Row>
          <Row term="Bill rate — the contract's">
            Set on the <Link to="/clients" className="underline">contract</Link>. It is what the
            client is charged for a guard-day, and payroll never reads it.
          </Row>
          <Row term="If a guard has no rate">
            Payroll falls back to the contract's pay rate. That is a safety net, not a setting to
            rely on — leave it in place across a whole detachment and everyone is paid the same
            peso regardless of rank.
          </Row>
          <Row term="The margin">
            Bill rate less pay rate, per guard-day. Because both sides derive from the same
            approved shifts, the margin on a site is arithmetic rather than an estimate.
          </Row>
        </div>
      </Section>

      {/* ------------------------------------------------------------------ */}
      <Section
        id="wages" n="B8" title="Wage orders and what a contract must cover"
        lead="A contract billed below what the guard on it legally costs is not a thin margin, it is an illegal contract — and under DO 150-16 the CLIENT is jointly and severally liable for the shortfall, which is why they ask to see this arithmetic."
      >
        <div className="border border-border bg-card p-4">
          <Row term="The wage order">
            Regional, dated, and set by the wage board.{" "}
            <Link to="/wage-orders" className="underline">Wage Orders</Link> holds them with their
            effectivity dates, and every lookup takes the one in force on the day being priced.
          </Row>
          <Row term="An enjoined order is not law">
            A wage order restrained by a court never took effect. It is kept on file marked
            <strong> enjoined</strong>, and it is excluded from every calculation — but it is not
            deleted, because the agency needs to be able to show it knew about it and why it was
            not applied.
          </Row>
          <Row term="The cost distribution">
            On a contract, <strong>Cost distribution</strong> builds the DOLE breakdown per guard:
            the daily wage, COLA, the 13th month, service incentive leave, the night differential
            and rest-day premiums the post actually attracts, uniform and equipment, and the
            employer&apos;s share of SSS, PhilHealth and Pag-IBIG — which is the part agencies
            leave out, because the guard never sees it on their payslip.
          </Row>
          <Row term="The 20% administrative fee">
            RA 11917 requires the agency fee to be at least 20% of the total contract cost. The
            screen checks it and says by how much it falls short.
          </Row>
          <Row term="Does the bill rate cover the cost?">
            The third check. If it does not, the screen gives the daily rate that would, rounded
            up — rounding it down produces a rate that fails its own coverage check by a centavo.
          </Row>
          <Row term="When an order takes effect">
            <strong>Impact</strong> on a wage order lists every contract priced under the old
            floor and what each would need to become.{" "}
            <strong>Escalate</strong> writes the new rate onto the contract as an amendment, so
            the change is dated and attributable rather than a field somebody edited.
          </Row>
        </div>
      </Section>

      <Section
        id="roles" n="B9" title="Who can do what"
        lead="Every action is tied to a role, and every action is written to the audit log with the name of the person who took it."
      >
        <div className="border border-border bg-card p-4">
          <Row term="Operations manager">Clients, contracts, sites, posts. The commercial side.</Row>
          <Row term="Scheduler">Builds and publishes the roster; fills open posts.</Row>
          <Row term="Field supervisor">Approves attendance, files and reviews incidents.</Row>
          <Row term="HR admin">Guards, documents, employment status, leave decisions.</Row>
          <Row term="Payroll admin">Cut-off periods, computation, adjustments, payslips.</Row>
          <Row term="Finance officer">Invoices and payments.</Row>
          <Row term="Auditor">Reads everything, changes nothing.</Row>
          <Row term="Guard">
            Their own phone only: their shifts, their attendance, their documents, their leave.
            They cannot see another guard's anything — the server enforces it, not the screen.
          </Row>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          The <Link to="/audit" className="underline">Audit Log</Link> records who did what and
          when, including the things that were refused.
        </p>
      </Section>

      <div className="border-t border-border pt-4 mt-10 text-xs text-muted-foreground">
        Figures on this page — the payroll factor and the statutory tables — are read from this
        system as you load it, not copied into the text. If someone changes them in Settings, this
        page changes with them.
      </div>
    </div>
  );
}
